import React, { useState } from 'react';
import PhotoModal from './components/PhotoModal';
import { FaCamera } from 'react-icons/fa';
import { StyleConstants } from './StyleConstants';

/**
 * PropertyCard component for displaying property listings in a consistent card format
 * Design based on modern real estate websites with consistent heights for all property types
 */
const PropertyCard = ({ listing, isMobile, onOpenModal }) => {
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  // Calculate the count of public photos
  const publicPhotoCount = (listing?.raw?.Media && Array.isArray(listing.raw.Media))
    ? listing.raw.Media.filter(m => m && m.MediaURL && m.Permission && Array.isArray(m.Permission) && m.Permission.includes('Public') && m.MediaCategory === 'Photo').length
    : 0;
  // Extract essential property details with fallbacks
  const {
    mlsIdentifier, // Actual MLS # (ListingId from Spark API)
    status,
    price,
    beds,
    baths,
    sqft,
    lotSizeAcres,
    photo,
    // propertyType, // Removed as it's no longer directly used for display after other changes
    SubdivisionName,
    city,
    state,
    postalCode,
  } = listing || {};

  // Determine if the property is lot/land to conditionally show beds/baths/sqft
  const isLotOrLand = (listing.propertyType || '').toLowerCase().includes('lot') || 
                      (listing.propertyType || '').toLowerCase().includes('land');

  // Format address components
  const unparsed = listing.address || listing.unparsedAddress || '';
  const streetAddress = (() => {
    // Try to get from unparsed address first
    if (unparsed && city) {
      const idx = unparsed.indexOf(city);
      if (idx > 0) {
        return unparsed.substring(0, idx).trim().replace(/,$/, '');
      }
    }
    
    // Fallback: build from raw fields if needed
    if (listing.raw) {
      const rn = listing.raw;
      return [rn.StreetNumber, rn.StreetDirPrefix, rn.StreetName, rn.StreetSuffix, rn.UnitNumber]
        .filter(Boolean)
        .join(' ')
        .replace(/  +/g, ' ')
        .trim();
    }
    
    return unparsed || 'Address unavailable';
  })();
  
  // Format city, state, zip
  let cityStateZip = '';
  
  // Check direct properties
  if (city || state || postalCode) {
    cityStateZip = [city, state, postalCode].filter(Boolean).join(', ');
  }
  // Try to get from raw data if available
  else if (listing.raw) {
    const r = listing.raw;
    cityStateZip = [r.City, r.StateOrProvince, r.PostalCode].filter(Boolean).join(', ');
  }
  
  // Format the zip code to appear after a space instead of a comma
  cityStateZip = cityStateZip.replace(/, ([0-9]{5})$/, ' $1');
  
  // Format price with locale string
  const formattedPrice = price ? `$${price.toLocaleString()}` : 'Price unavailable';
  
  // Get neighborhood or subdivision, fallback to city if not available
  const neighborhood = (() => {
    // Check if SubdivisionName exists directly on listing
    if (SubdivisionName && SubdivisionName !== 'N/A' && SubdivisionName.trim() !== '') {
      return SubdivisionName;
    }
    
    // Try to get subdivision from raw data
    if (listing.raw && listing.raw.SubdivisionName && 
        listing.raw.SubdivisionName !== 'N/A' && 
        listing.raw.SubdivisionName.trim() !== '') {
      return listing.raw.SubdivisionName;
    }
    
    return city || '';
  })();
  
  // propertyTypeFromApi, propertySubType, and displayType declarations were removed as they are no longer used.
  
  return (
    <>
      <div className="property-card">
        {/* Card styles applied inline to maintain consistency */}
        <div style={{
          ...StyleConstants.propertyCard,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          maxWidth: '100%' // Prevent overflow on smaller screens
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
        }}>
          {/* Property Image Section with consistent aspect ratio and photo count */} 
          <div style={{
            position: 'relative',
            paddingBottom: '65%', /* Maintain consistent aspect ratio */
            background: '#f0f0f0',
            overflow: 'hidden', // Ensures the image corners are rounded if image is larger than container
            cursor: 'pointer', // Added for clickability for modal
            WebkitTapHighlightColor: 'transparent' // Prevent tap highlight on this container
          }} onClick={() => {
            if (typeof onOpenModal === 'function') onOpenModal();
            setShowPhotoModal(true);
          }}>
            {/* Photo Count Badge */}
            {listing && listing.raw && listing.raw.Media && publicPhotoCount > 0 && (
              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                zIndex: 1 // Ensure it's above the image
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16" style={{ marginRight: '4px' }}><path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1v6zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828-.828A2 2 0 0 1 3.172 4H2z"/><path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm0-4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/></svg>
                {publicPhotoCount}
              </div>
            )}
            {photo ? (
              <img 
                src={photo} 
                alt={`${streetAddress}`}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  top: 0,
                  left: 0,
                  cursor: 'pointer'
                  // WebkitTapHighlightColor removed from here, moved to parent
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPhotoModal(true);
                }}
              />
            ) : (
              <div style={{ 
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#888',
                fontSize: '14px',
              }}>
                No image available
              </div>
            )}
            
            {/* Status Badge */}
            {status && (
              <div style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: '600',
                backgroundColor: 
                  status === 'Active' ? '#4CAF50' : 
                  status === 'Pending' ? '#FFC107' : 
                  status === 'Closed' ? '#757575' : 
                  '#1976D2',
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}>
                {status}
              </div>
            )}
          </div>
          
          {/* Property Details Section - Consistent height */}
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            {/* Price and MLS # Row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center', // Vertically align price and MLS#
              marginBottom: '8px', // Keep overall bottom margin for the row
            }}>
              {/* Price */}
              <div style={{
                fontSize: '22px',
                fontWeight: 'bold',
                color: '#222',
                lineHeight: '1.2',
              }}>
                {formattedPrice}
              </div>
              {/* MLS Number */}
              {mlsIdentifier && ( // Render only if mlsIdentifier (MLS#) exists
                <div style={{
                  fontSize: '12px', // Small text
                  color: '#555',    // Muted color for subtlety
                  marginLeft: '10px' // Space between price and MLS#
                }}>
                  MLS # {mlsIdentifier}
                </div>
              )}
            </div>
            
            {/* Key Details - Consistent Height Section */}
            <div style={{ 
              display: 'flex', 
              marginBottom: '12px', 
              height: '24px', /* Fixed height regardless of property type */
              alignItems: 'center'
            }}>
              {isLotOrLand ? (
                /* Land Property Display - Formatted like bed/bath/sqft */
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '16px',
                  color: '#444',
                }}>
                  {lotSizeAcres != null && (
                    <span>
                      <span style={{ fontWeight: '600' }}>{lotSizeAcres}</span> acres
                    </span>
                  )}
                </div>
              ) : (
                /* Residential Property Display */
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '16px',
                  color: '#444',
                }}>
                  {beds != null && (
                    <span>
                      <span style={{ fontWeight: '600' }}>{beds}</span> bed
                    </span>
                  )}
                  {beds != null && baths != null && (
                    <span style={{ margin: '0 4px', color: '#888' }}>•</span>
                  )}
                  {baths != null && (
                    <span>
                      <span style={{ fontWeight: '600' }}>{baths}</span> bath
                    </span>
                  )}
                  {baths != null && sqft && (
                    <span style={{ margin: '0 4px', color: '#888' }}>•</span>
                  )}
                  {sqft && (
                    <span>
                      <span style={{ fontWeight: '600' }}>{sqft.toLocaleString()}</span> sqft
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Address - Fixed Height Regardless of Content */}
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              height: '44px', /* Fixed height for address section */
              marginBottom: '8px',
              overflow: 'hidden',
            }}>
              <div style={{ 
                fontSize: '16px',
                fontWeight: '500',
                color: '#333',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {streetAddress}
              </div>
              <div style={{ 
                fontSize: '14px',
                color: '#666',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {cityStateZip}
              </div>
            </div>
            
            {/* Neighborhood/Subdivision - Important to include as requested */}
            {neighborhood && (
              <div style={{
                fontSize: '14px',
                color: '#555',
                marginBottom: '0',
                display: 'flex',
                alignItems: 'center',
                height: '24px',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '4px'}}>
                  <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#555"/>
                </svg>
                <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{neighborhood}</span>
              </div>
            )}
            
            {/* Additional Details Section removed as per user request */}
          </div>
        </div>
      </div>
      
      {/* Photo Modal */}
      {showPhotoModal && (
        <PhotoModal
          listing={listing}
          onClose={() => setShowPhotoModal(false)}
          isMobile={isMobile}
        />
      )}
    </>
  );
};

export default PropertyCard;
